'use client';
import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useRouter } from 'next/navigation';

import { APP_ROUTE } from '@src/constants/routes';
import Button from '@src/components/coreUI/button';
import Cross from '@src/assets/svg/cross';
import { userTypeDialog } from '@src/interfaces/components/selectUserTypeDialog';

import {
  USERS_ROLE_WITH_DETAIL,
  ZEME_USERS_ROLE,
} from '@src/constants/configs';
import useTheme from '@src/hooks/useTheme';
import { themeType } from '@src/interfaces/theme';

/**
 * Dialog to select user type[Renter, Agent, Owner] from user.
 * @param {Boolean} Props.isVisible
 * @param {Function} Props.onClose
 * @returns Node
 */
const UserTypeDialog = ({
  actionType = '',
  isVisible = false,
  isHideRenter = false,
  onClose = () => {
    return;
  },
}: userTypeDialog) => {
  // hooks
  const { push } = useRouter();
  const { setTheme }: themeType = useTheme();

  // state
  const [userSelection, setUserSelection] = useState(
    isHideRenter ? ZEME_USERS_ROLE.AGENT : ZEME_USERS_ROLE.RENTER
  );

  /**
   * Handle continue button click
   */
  const handleSubmit = () => {
    // Set Theme
    setTheme(userSelection);

    // Redirection
    switch (true) {
      case actionType === 'login' && userSelection === ZEME_USERS_ROLE.RENTER:
        push(APP_ROUTE.LOGIN);
        break;
      case actionType === 'login' &&
        (userSelection === ZEME_USERS_ROLE.AGENT ||
          userSelection === ZEME_USERS_ROLE.LANDLORD):
        push(APP_ROUTE.LOGIN);
        break;
      case actionType === 'register' &&
        userSelection === ZEME_USERS_ROLE.RENTER:
        push(APP_ROUTE.RENTER_REGISTER);
        break;
      case actionType === 'register' && userSelection === ZEME_USERS_ROLE.AGENT:
        push(APP_ROUTE.AGENT_REGISTER);
        break;
      case actionType === 'register' &&
        userSelection === ZEME_USERS_ROLE.LANDLORD:
        push(APP_ROUTE.LANDLORD_REGISTER);
        break;
      default:
        break;
    }
  };

  return (
    <Transition.Root show={isVisible} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black-dark/30 transition-opacity backdrop-blur-[1px]" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-xl bg-white pt-5 text-left shadow-xl transition-all sm:my-8 w-full sm:w-full max-w-full sm:max-w-2xl py-8 sm:px-8 px-5">
                <div>
                  <a
                    onClick={() => onClose()}
                    className="absolute block w-[1.875rem] h-[1.875rem] top-[-10px] right-[-7px] rounded-full text-blue-dark hover:text-blue-dark/70 focus-visible:outline-none cursor-pointer"
                  >
                    <Cross />
                  </a>
                  <Dialog.Title
                    as="h3"
                    className="text-black-dark font-semibold text-22 text-center mt-7"
                  >
                    Are you a...
                  </Dialog.Title>
                  <div
                    className={`max-w-[${isHideRenter ? 400 : 600}px] m-auto`}
                  >
                    <div
                      className={`mt-9 grid grid-cols-1 xs:grid-cols-${
                        isHideRenter ? 2 : 3
                      } gap-3`}
                    >
                      {Object.values(USERS_ROLE_WITH_DETAIL)
                        .filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (el: any) => {
                            if (
                              isHideRenter &&
                              el.value === ZEME_USERS_ROLE.RENTER
                            ) {
                              return false;
                            }

                            return true;
                          }
                        )
                        .map(
                          (
                            {
                              label = '',
                              value = '',
                              subLabel = '',
                              image: Icon,
                            },
                            index
                          ) => {
                            return (
                              <div
                                className={`border px-4 pb-4 pt-4 xs:pt-10 rounded-lg shadow-container cursor-pointer flex items-center justify-between xs:block ${
                                  userSelection === value
                                    ? 'border-blue-dark'
                                    : null
                                }`}
                                key={index}
                                onClick={() => setUserSelection(value)}
                              >
                                <div className="xs:mb-8 scale-75 xs:scale-100 xs:h-24 flex items-center justify-center">
                                  <Icon />
                                </div>
                                <div className="bg-pink-dark/10 text-pink-dark rounded-md text-center text-lg font-medium min-w-[120px] flex flex-col min-h-[84px] justify-center items-center">
                                  <span className="leading-[20px]">
                                    {label}
                                  </span>
                                  <span className="text-11 leading-[20px]">
                                    {subLabel}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                        )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center mb-4 mt-14">
                  <Button variant="primary" onClick={handleSubmit}>
                    Continue
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default UserTypeDialog;
